export const DELIVERY_SUMMARY_FIELDS = Object.freeze([
  "Status",
  "Branch",
  "Changes",
  "Validation",
  "Known limitations"
] as const);

export function buildDeliverySummary({ evidence }: { evidence: any }): Record<string, string> {
  const commands = (evidence?.commands || []).map((item: any) => `${item.command || item.name}: ${item.status}`).join("; ") || "No validation commands recorded";
  return {
    Status: evidence?.status || "BLOCKED",
    Branch: evidence?.branch || "unknown",
    Changes: (evidence?.deliveryChangedFiles || evidence?.changedFiles || []).join(", ") || "No changed files recorded",
    Validation: commands,
    "Known limitations": (evidence?.limitations || []).join("; ") || "None recorded"
  };
}

export interface ValidationSummaryResult {
  status: "PASS" | "FAIL";
  missing: string[];
  invalid: string[];
}

export function validateDeliverySummary(summary: any): ValidationSummaryResult {
  const missing = DELIVERY_SUMMARY_FIELDS.filter((field) => !String(summary?.[field] || "").trim());
  const status = String(summary?.Status || "").trim();
  const invalid: string[] = [];
  if (!["COMPLETED", "COMPLETED_WITH_NOTES", "BLOCKED"].includes(status)) invalid.push("Status");
  if (status !== "BLOCKED" && /FAIL|BLOCKED/.test(String(summary?.Validation || ""))) invalid.push("successful status with failed validation");
  if (status !== "BLOCKED" && /No validation commands recorded/i.test(String(summary?.Validation || ""))) {
    invalid.push("successful status without validation evidence");
  }
  if (status === "BLOCKED") {
    const diagnostic = String(summary?.["Known limitations"] || "").trim();
    if (!/Gate:/i.test(diagnostic) || !/Observed:/i.test(diagnostic) || !/Expected:/i.test(diagnostic) || !/Next action:/i.test(diagnostic)) {
      invalid.push("blocked status without a structured diagnostic");
    }
  }
  return { status: missing.length || invalid.length ? "FAIL" : "PASS", missing, invalid };
}

export function formatDeliverySummary(summary: any): string {
  return DELIVERY_SUMMARY_FIELDS.map((field) => `${field}: ${summary?.[field] || "NOT_RECORDED"}`).join("\n");
}

// Backward-compatible aliases for integrations that imported the previous API.
export const CANONICAL_FINALIZATION_FIELDS = DELIVERY_SUMMARY_FIELDS;
export const buildCanonicalFinalization = ({ collectorStatus = "BLOCKED", branchRecovery = "unknown" } = {}): Record<string, string> => ({
  Status: collectorStatus === "PASS" ? "COMPLETED" : collectorStatus === "PASS_WITH_NOTES" ? "COMPLETED_WITH_NOTES" : "BLOCKED",
  Branch: branchRecovery,
  Changes: "See observed diff",
  Validation: collectorStatus,
  "Known limitations": "See evidence"
});
export const validateCanonicalFinalization = validateDeliverySummary;
export const formatCanonicalFinalization = formatDeliverySummary;
